import classNames from 'classnames';
import { type MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type Board, type CardSize, type PublicBoard } from '@air/api/types';
import { type JustifiedBox } from '@air/component-gallery-view';
import { Tooltip } from '@air/primitive-tooltip';

import { CustomFieldListWithOverflow } from '~/components/CustomFields/CustomFieldListWithOverflow';
import { GalleryItemType } from '~/components/Gallery/types';
import { type LibraryCustomField } from '~/components/LibraryBeta/hooks/queries/useLibraryCustomFields';
import { GalleryBoardCustomFieldAction } from '~/components/v2/components/shared/gallery/GalleryBoardCustomFieldAction';
import { GalleryBoardMenu } from '~/components/v2/components/shared/gallery/GalleryBoardMenu';
import { GalleryBoardThumbnail } from '~/components/v2/components/shared/gallery/GalleryBoardThumbnail';
import {
  GallerySelectableCard,
  type GallerySelectableCardProps,
} from '~/components/v2/components/shared/gallery/GallerySelectableCard';
import { currentCardSizeSelector, visibleCustomFieldsViewOptionsSelector } from '~/store/configViews/selectors';
import { type SelectableGalleryBoardItem } from '~/store/selectedItems/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type GalleryBoardProps<B extends Board | PublicBoard> = Pick<
  GallerySelectableCardProps<SelectableGalleryBoardItem<B>>,
  'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable' | 'onClick'
> & {
  ancestors?: string;
  box: JustifiedBox;
  board: B;
  canViewCustomFields?: boolean;
  libraryCustomFields?: LibraryCustomField[];
  onCmdClickTitle?: () => void;
  size?: CardSize;
};

const _GalleryBoard = <B extends Board | PublicBoard>({
  ancestors,
  box,
  board,
  canViewCustomFields,
  getItemMenuOptions,
  getSelectionMenuOptions,
  libraryCustomFields,
  isSelectable,
  onClick,
  onCmdClickTitle,
  size = 'medium',
}: GalleryBoardProps<B>) => {
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
  const configViewCardSize = useSelector(currentCardSizeSelector);
  const [hasValidThumbnail, setHasValidThumbnail] = useState(false);

  const filteredLibraryCustomFields = useMemo(() => {
    if (libraryCustomFields?.length) {
      return board.customFields?.filter((boardField) => {
        return !!libraryCustomFields.find((customField) => customField.id === boardField.id)?.visible;
      });
    }

    return board.customFields;
  }, [board.customFields, libraryCustomFields]);

  const filteredVisibleCustomFields = useMemo(() => {
    return filteredLibraryCustomFields?.filter((customField) =>
      visibleCustomFields.find((vcf) => vcf.customFieldId === customField.id),
    );
  }, [filteredLibraryCustomFields, visibleCustomFields]);

  const item = useMemo<SelectableGalleryBoardItem<B>>(
    () => ({
      id: board.id,
      item: board,
      type: GalleryItemType.board,
    }),
    [board],
  );

  const onTitleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if ((event.metaKey || event.ctrlKey) && onCmdClickTitle) {
        onCmdClickTitle();
      } else {
        onClick();
      }
    },
    [onClick, onCmdClickTitle],
  );

  const onFallbackChange = useCallback((isFallback: boolean) => {
    setHasValidThumbnail(!isFallback);
  }, []);

  return (
    <GallerySelectableCard
      data-testid="BOARD_CARD"
      // The card radii read the --gallery-card-* vars (set by the public collection page's Corner
      // rounding setting — see shareLinkThumbnailRadius.ts) and fall back to the native values, so
      // every surface that doesn't set the vars renders exactly as before.
      className="h-full rounded-[var(--gallery-card-radius,12px)] ads:bg-base bg-gray-1 p-1 ads:ring-ads-gray-5 ring-1 ring-gray-5 ring-inset ring-offset-0 transition-colors ads:data-[hovering=true]:bg-hover-subtle ads:data-[selected=true]:bg-hover-subtle data-[hovering=true]:bg-gray-4 data-[selected=true]:bg-gray-4 ads:data-[highlighted=true]:ring-focus ads:data-[selected=true]:ring-primary data-[highlighted=true]:ring-2 data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-2 data-[selected=true]:ring-blue-9"
      data-size={size}
      data-valid-thumbnail={hasValidThumbnail}
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      item={item}
      isSelectable={isSelectable}
      onClick={onClick}
    >
      {({ isHovering, setIsMenuOpen }) => (
        <div className="size-full">
          <GalleryBoardThumbnail board={board} box={box} sizes="100vw" onFallbackChange={onFallbackChange} />

          <div className="pointer-events-none absolute inset-x-1 top-1 flex items-center justify-between p-2">
            <div className="pointer-events-auto">
              {!!filteredLibraryCustomFields?.length && canViewCustomFields && (
                <GalleryBoardCustomFieldAction customFields={filteredLibraryCustomFields} />
              )}
            </div>

            <div className="pointer-events-auto">
              {isHovering && (
                <GalleryBoardMenu
                  board={board}
                  getItemMenuOptions={getItemMenuOptions}
                  getSelectionMenuOptions={getSelectionMenuOptions}
                  setIsMenuOpen={setIsMenuOpen}
                />
              )}
            </div>
          </div>
          <div className="pointer-events-none absolute inset-x-1 bottom-1 z-20 flex flex-col items-start gap-0.5 rounded-b-[var(--gallery-card-inner-radius,8px)] bg-transparent px-2 pt-6 pb-2 group-data-[valid-thumbnail=true]/gallery-selectable-card:bg-gradient-to-t group-data-[valid-thumbnail=true]/gallery-selectable-card:from-black/70 group-data-[valid-thumbnail=true]/gallery-selectable-card:to-transparent">
            <Tooltip align="start" label={board.title} side="top">
              <button
                className={classNames(
                  // The --headline-font-* vars exist only on the public collection and board page
                  // roots (their headline-font design setting); everywhere else they are unset and
                  // the fallbacks apply — `inherit` matches the preflight `button { font-family:
                  // inherit }`, and 600/normal are this title's own pre-var weight and style. The
                  // weight is read rather than hardcoded so the applied weight is always the face
                  // the loader requested (a hardcoded 600 against a regular-only load renders as
                  // browser-synthesized fake bold).
                  'pointer-events-auto max-w-full text-left font-[family-name:var(--headline-font-family,inherit)] text-18 leading-[24px] [font-style:var(--headline-font-style,normal)] [font-weight:var(--headline-font-weight,600)] hover:underline group-data-[size=small]/gallery-selectable-card:text-16 group-data-[size=small]/gallery-selectable-card:leading-[20px]',
                  hasValidThumbnail ? 'text-white' : 'ads:text-secondary text-gray-11',
                )}
                data-testid="GALLERY_BOARD_TITLE"
                onClick={onTitleClick}
                type="button"
              >
                <span
                  className={
                    configViewCardSize === 'large' || configViewCardSize === 'extra-large'
                      ? 'line-clamp-3'
                      : 'line-clamp-2'
                  }
                  style={hasValidThumbnail ? { textShadow: '0px 0px 4px rgba(0, 0, 0, 0.40)' } : undefined}
                >
                  {board.title}
                </span>
              </button>
            </Tooltip>
            {!!ancestors && (
              <span
                className={classNames(
                  'text-12 leading-[18px]',
                  hasValidThumbnail ? 'text-white' : 'ads:text-secondary text-gray-11',
                )}
              >
                in {ancestors}
              </span>
            )}
            {!!filteredVisibleCustomFields?.length && canViewCustomFields && (
              <CustomFieldListWithOverflow
                allowedWidth={box.width}
                customFields={filteredVisibleCustomFields}
                hasDarkBackground
                shouldShowTooltip
                size="small"
              />
            )}
          </div>
        </div>
      )}
    </GallerySelectableCard>
  );
};

// biome-ignore lint/plugin: memo export preserves generic props typing for GalleryBoard
export const GalleryBoard = memo(_GalleryBoard) as typeof _GalleryBoard;
